import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import io from 'socket.io-client';

function OrderManagement() {
  const [orders, setOrders] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingCurrentPage, setPendingCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pendingTotalPages, setPendingTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState('all');
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    pair: ''
  });
  const [editingOrder, setEditingOrder] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [profitMargin, setProfitMargin] = useState(0);
  const [userHoldings, setUserHoldings] = useState({});
  const [holdingsLoading, setHoldingsLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [holdingsUpdateCount, setHoldingsUpdateCount] = useState(0);
  
  // Real-time states
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(10000);
  const [lastManualRefresh, setLastManualRefresh] = useState(null);
  const [updateCount, setUpdateCount] = useState(0);
  const [connectionQuality, setConnectionQuality] = useState('good');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const socketRef = useRef(null);
  const autoRefreshIntervalRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  // Cleanup intervals on component unmount
  useEffect(() => {
    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Setup auto-refresh interval
  useEffect(() => {
    console.log('🔄 Auto-refresh config changed:', { autoRefresh, refreshInterval });
    
    // Clear existing interval
    if (autoRefreshIntervalRef.current) {
      clearInterval(autoRefreshIntervalRef.current);
      autoRefreshIntervalRef.current = null;
    }

    // Setup new interval if auto-refresh is enabled
    if (autoRefresh) {
      console.log(`🔄 Setting up auto-refresh every ${refreshInterval}ms`);
      
      autoRefreshIntervalRef.current = setInterval(() => {
        console.log('🔄 Auto-refresh triggered');
        refreshAllData(false);
      }, refreshInterval);
    } else {
      console.log('🔄 Auto-refresh disabled');
    }

    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
    };
  }, [autoRefresh, refreshInterval]);

  useEffect(() => {
    initializeSocket();
    fetchUserHoldings();
  }, []);

  useEffect(() => {
    if (activeTab === 'all') {
      fetchOrders();
    } else {
      fetchPendingOrders();
    }
  }, [currentPage, pendingCurrentPage, filters, activeTab]);

  useEffect(() => {
    if (isConnected && socketRef.current) {
      // Listen for order updates from backend
      socketRef.current.on('orderUpdate', (updatedOrder) => {
        console.log('🔄 REAL-TIME ORDER UPDATE:', updatedOrder);
        handleOrderUpdate(updatedOrder);
        setUpdateCount(prev => prev + 1);
        
        // EMIT GLOBAL UPDATE - This ensures Trading component receives the update
        emitGlobalOrderUpdate(updatedOrder);
      });

      socketRef.current.on('tradeExecuted', (trade) => {
        console.log('🔄 REAL-TIME TRADE EXECUTED:', trade);
        handleTradeExecution(trade);
        setUpdateCount(prev => prev + 1);
        
        // EMIT GLOBAL UPDATE
        emitGlobalTradeUpdate(trade);
      });

      socketRef.current.on('holdingsUpdated', (holdingsData) => {
        console.log('🔄 REAL-TIME HOLDINGS UPDATE:', holdingsData);
        handleHoldingsUpdate(holdingsData);
        setUpdateCount(prev => prev + 1);
        
        // EMIT GLOBAL UPDATE
        emitGlobalHoldingsUpdate(holdingsData);
      });

      socketRef.current.on('orderRejected', (rejectionData) => {
        console.log('🔄 REAL-TIME ORDER REJECTED:', rejectionData);
        handleOrderRejection(rejectionData);
        setUpdateCount(prev => prev + 1);
        
        // EMIT GLOBAL UPDATE
        emitGlobalOrderRejection(rejectionData);
      });

      socketRef.current.on('userHoldingsUpdated', (userHoldingsData) => {
        console.log('🔄 REAL-TIME USER HOLDINGS UPDATE:', userHoldingsData);
        handleUserHoldingsUpdate(userHoldingsData);
        setUpdateCount(prev => prev + 1);
        
        // EMIT GLOBAL UPDATE
        emitGlobalUserHoldingsUpdate(userHoldingsData);
      });

      socketRef.current.on('orderCreated', (newOrder) => {
        console.log('🔄 REAL-TIME ORDER CREATED:', newOrder);
        handleOrderCreated(newOrder);
        setUpdateCount(prev => prev + 1);
        
        // EMIT GLOBAL UPDATE
        emitGlobalOrderCreated(newOrder);
      });

      // NEW: Listen for order status changes from admin actions
      socketRef.current.on('orderStatusChanged', (orderData) => {
        console.log('🔄 ORDER STATUS CHANGED:', orderData);
        handleOrderStatusChange(orderData);
        setUpdateCount(prev => prev + 1);
        
        // EMIT GLOBAL UPDATE
        emitGlobalOrderStatusChange(orderData);
      });

      socketRef.current.on('pong', (latency) => {
        const newQuality = latency < 100 ? 'good' : latency < 300 ? 'poor' : 'disconnected';
        setConnectionQuality(newQuality);
      });
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.off('orderUpdate');
        socketRef.current.off('tradeExecuted');
        socketRef.current.off('holdingsUpdated');
        socketRef.current.off('orderRejected');
        socketRef.current.off('userHoldingsUpdated');
        socketRef.current.off('orderCreated');
        socketRef.current.off('orderStatusChanged');
        socketRef.current.off('pong');
      }
    };
  }, [isConnected]);

  // NEW: Emit global events that Trading component can listen to
  const emitGlobalOrderUpdate = (order) => {
    if (socketRef.current) {
      socketRef.current.emit('broadcastOrderUpdate', order);
    }
  };

  const emitGlobalTradeUpdate = (trade) => {
    if (socketRef.current) {
      socketRef.current.emit('broadcastTradeUpdate', trade);
    }
  };

  const emitGlobalHoldingsUpdate = (holdings) => {
    if (socketRef.current) {
      socketRef.current.emit('broadcastHoldingsUpdate', holdings);
    }
  };

  const emitGlobalOrderRejection = (rejection) => {
    if (socketRef.current) {
      socketRef.current.emit('broadcastOrderRejection', rejection);
    }
  };

  const emitGlobalUserHoldingsUpdate = (holdings) => {
    if (socketRef.current) {
      socketRef.current.emit('broadcastUserHoldingsUpdate', holdings);
    }
  };

  const emitGlobalOrderCreated = (order) => {
    if (socketRef.current) {
      socketRef.current.emit('broadcastOrderCreated', order);
    }
  };

  const emitGlobalOrderStatusChange = (order) => {
    if (socketRef.current) {
      socketRef.current.emit('broadcastOrderStatusChange', order);
    }
  };

  const initializeSocket = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found for WebSocket connection');
        return;
      }

      const newSocket = io('https://trcsite.net', {
        auth: {
          token: token
        },
        transports: ['websocket', 'polling'],
        timeout: 10000,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000
      });

      socketRef.current = newSocket;

      newSocket.on('connect', () => {
        console.log('✅ WebSocket connected for order management');
        setIsConnected(true);
        setConnectionQuality('good');
        
        const user = JSON.parse(localStorage.getItem('user'));
        if (user && user.id) {
          newSocket.emit('userAuthenticated', user.id);
          // Join admin room for broadcasting
          newSocket.emit('joinAdminRoom');
        }

        startLatencyMonitoring();
      });

      newSocket.on('disconnect', (reason) => {
        console.log('❌ WebSocket disconnected:', reason);
        setIsConnected(false);
        setConnectionQuality('disconnected');
      });

      newSocket.on('connect_error', (error) => {
        console.error('❌ WebSocket connection error:', error);
        setIsConnected(false);
        setConnectionQuality('disconnected');
      });

      newSocket.on('reconnect_attempt', (attempt) => {
        console.log(`🔄 WebSocket reconnection attempt ${attempt}`);
        setConnectionQuality('poor');
      });

      newSocket.on('reconnect', (attempt) => {
        console.log(`✅ WebSocket reconnected after ${attempt} attempts`);
        setIsConnected(true);
        setConnectionQuality('good');
        // Rejoin admin room after reconnection
        socketRef.current.emit('joinAdminRoom');
      });

      newSocket.on('error', (error) => {
        console.error('❌ WebSocket error:', error);
      });

      setSocket(newSocket);

    } catch (error) {
      console.error('Error initializing WebSocket:', error);
    }
  };

  const startLatencyMonitoring = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(() => {
      if (socketRef.current && socketRef.current.connected) {
        const start = Date.now();
        socketRef.current.emit('ping', () => {
          const latency = Date.now() - start;
          socketRef.current.emit('pong', latency);
        });
      }
    }, 5000);
  };

  // Enhanced polling with connection quality awareness
  useEffect(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    const pollingInterval = setInterval(() => {
      if (!isConnected || connectionQuality === 'poor') {
        console.log('🔄 Polling for updates (WebSocket disconnected or poor connection)');
        refreshAllData(false);
      }
    }, connectionQuality === 'poor' ? 5000 : 10000);

    pollingIntervalRef.current = pollingInterval;

    return () => clearInterval(pollingInterval);
  }, [isConnected, connectionQuality]);

  const toggleAutoRefresh = () => {
    const newAutoRefresh = !autoRefresh;
    console.log('🔄 Auto-refresh toggled:', newAutoRefresh);
    setAutoRefresh(newAutoRefresh);
    
    if (newAutoRefresh) {
      refreshAllData(false);
    }
  };

  const handleIntervalChange = (interval) => {
    console.log('🔄 Refresh interval changed to:', interval);
    setRefreshInterval(interval);
  };

  const refreshAllData = async (showLoading = true) => {
    if (isRefreshing) {
      console.log('🔄 Refresh already in progress, skipping...');
      return;
    }

    try {
      console.log('🔄 Refresh triggered');
      setIsRefreshing(true);
      setLastManualRefresh(new Date());
      
      if (activeTab === 'all') {
        await fetchOrders(showLoading);
      } else {
        await fetchPendingOrders(showLoading);
      }
      
      await fetchUserHoldings();
      
      setUpdateCount(prev => prev + 1);
      setLastUpdate(new Date());
      console.log('✅ Refresh completed successfully');
    } catch (error) {
      console.error('❌ Refresh failed:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleOrderUpdate = (updatedOrder) => {
    setLastUpdate(new Date());
    
    setOrders(prevOrders => 
      prevOrders.map(order => 
        order.id === updatedOrder.id ? { ...order, ...updatedOrder } : order
      )
    );

    setPendingOrders(prevPending => 
      prevPending.filter(order => order.id !== updatedOrder.id)
    );

    if (updatedOrder.status === 'filled') {
      setTimeout(() => {
        fetchUserHoldings();
      }, 1000);
    }
  };

  // NEW: Handle order status changes from admin actions
  const handleOrderStatusChange = (orderData) => {
    setLastUpdate(new Date());
    
    setOrders(prevOrders => 
      prevOrders.map(order => 
        order.id === orderData.id ? { ...order, ...orderData } : order
      )
    );

    setPendingOrders(prevPending => 
      prevPending.filter(order => order.id !== orderData.id)
    );

    // Refresh data to ensure consistency
    setTimeout(() => {
      refreshAllData(false);
    }, 500);
  };

  const handleOrderCreated = (newOrder) => {
    setLastUpdate(new Date());
    
    if (newOrder.status === 'pending') {
      setPendingOrders(prevPending => [newOrder, ...prevPending]);
    } else {
      setOrders(prevOrders => [newOrder, ...prevOrders]);
    }
  };

  const handleTradeExecution = (trade) => {
    setLastUpdate(new Date());
    
    setOrders(prevOrders =>
      prevOrders.map(order =>
        order.id === trade.order_id ? { ...order, status: 'filled', filled_amount: trade.amount } : order
      )
    );

    setPendingOrders(prevPending =>
      prevPending.filter(order => order.id !== trade.order_id)
    );

    setTimeout(() => {
      fetchUserHoldings();
    }, 500);
  };

  const handleHoldingsUpdate = (holdingsData) => {
    setLastUpdate(new Date());
    
    setTimeout(() => {
      fetchUserHoldings();
    }, 300);
  };

  const handleUserHoldingsUpdate = (userHoldingsData) => {
    setLastUpdate(new Date());
    setHoldingsUpdateCount(prev => prev + 1);
    
    if (userHoldingsData.userId && userHoldingsData.holdings) {
      setUserHoldings(prevHoldings => {
        const newHoldings = { ...prevHoldings };
        
        if (!newHoldings[userHoldingsData.userId]) {
          newHoldings[userHoldingsData.userId] = {};
        }
        
        Object.keys(userHoldingsData.holdings).forEach(currency => {
          newHoldings[userHoldingsData.userId][currency] = {
            ...newHoldings[userHoldingsData.userId][currency],
            ...userHoldingsData.holdings[currency],
            last_updated: new Date().toISOString()
          };
        });
        
        return newHoldings;
      });
    }
  };

  const handleOrderRejection = (rejectionData) => {
    setLastUpdate(new Date());
    
    setOrders(prevOrders =>
      prevOrders.map(order =>
        order.id === rejectionData.orderId ? { ...order, status: 'rejected', rejection_reason: rejectionData.error } : order
      )
    );

    setPendingOrders(prevPending =>
      prevPending.filter(order => order.id !== rejectionData.orderId)
    );
  };

  const fetchUserHoldings = async () => {
    try {
      setHoldingsLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get('https://trcsite.net/api/admin/user-holdings-detailed', {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000
      });
      
      if (response.data.holdings) {
        const holdingsMap = {};
        response.data.holdings.forEach(holding => {
          holdingsMap[holding.user_id] = holdingsMap[holding.user_id] || {};
          holdingsMap[holding.user_id][holding.currency] = {
            ...holding,
            last_updated: new Date().toISOString()
          };
        });
        
        setUserHoldings(holdingsMap);
        setHoldingsUpdateCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error fetching user holdings:', error);
    } finally {
      setHoldingsLoading(false);
    }
  };

  const fetchOrders = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: 20,
        ...filters
      }).toString();

      const response = await axios.get(`https://trcsite.net/api/admin/orders?${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOrders(response.data.orders);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingOrders = async (showLoading = true) => {
    try {
      if (showLoading) {
        setPendingLoading(true);
      }
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://trcsite.net/api/admin/orders/pending?page=${pendingCurrentPage}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPendingOrders(response.data.orders);
      setPendingTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching pending orders:', error);
    } finally {
      setPendingLoading(false);
    }
  };

  const getActualBuyPriceDisplay = (order) => {
    if (order.type !== 'sell') return null;
    
    const baseCurrency = order.pair?.split('/')[0];
    const userHolding = userHoldings[order.user_id]?.[baseCurrency];
    
    if (!userHolding) {
      return null;
    }
    
    const firstBuyPrice = userHolding.first_buy_price ? parseFloat(userHolding.first_buy_price) : null;
    const lastBuyPrice = userHolding.last_buy_price ? parseFloat(userHolding.last_buy_price) : null;
    const weightedAvgPrice = parseFloat(userHolding.average_buy_price) || 0;
    
    const result = {
      firstBuyPrice: firstBuyPrice,
      lastBuyPrice: lastBuyPrice,
      weightedAvgPrice: weightedAvgPrice,
      displayPrice: lastBuyPrice || weightedAvgPrice,
      priceType: lastBuyPrice ? 'last_buy' : 'weighted_avg',
      totalQuantity: parseFloat(userHolding.total_quantity || 0),
      totalTrades: userHolding.trade_details?.length || userHolding.price_stats?.total_trades || 0,
      lastUpdated: userHolding.last_updated
    };
    
    return result;
  };

  const calculateProfitMargin = (order) => {
    if (order.type !== 'sell') return null;

    const baseCurrency = order.pair?.split('/')[0];
    if (!baseCurrency) return null;

    const priceInfo = getActualBuyPriceDisplay(order);
    if (!priceInfo || !priceInfo.displayPrice) return null;

    const currentPrice = parseFloat(order.price) || (parseFloat(order.total) / parseFloat(order.amount));
    const actualBuyPrice = typeof priceInfo.displayPrice === 'number' 
      ? priceInfo.displayPrice 
      : parseFloat(priceInfo.displayPrice) || 0;
    
    if (!currentPrice || currentPrice <= 0 || !actualBuyPrice || actualBuyPrice <= 0) {
      return null;
    }

    const profitMargin = ((currentPrice - actualBuyPrice) / actualBuyPrice) * 100;
    
    return {
      margin: profitMargin,
      buyPrice: actualBuyPrice,
      priceType: priceInfo.priceType,
      lastUpdated: priceInfo.lastUpdated
    };
  };

  const getProfitMarginInfo = (profitData, order) => {
    if (!profitData) {
      const baseCurrency = order.pair?.split('/')[0];
      const userHolding = userHoldings[order.user_id]?.[baseCurrency];
      
      if (order.type === 'sell') {
        if (!userHolding) {
          return {
            color: '#ff9800',
            text: 'No holdings',
            icon: '📭',
            tooltip: 'User has never bought this cryptocurrency'
          };
        } else if (!userHolding.last_buy_price || userHolding.last_buy_price <= 0) {
          return {
            color: '#ff9800',
            text: 'No buy history',
            icon: '💰',
            tooltip: 'User has no purchase history for this coin'
          };
        }
      }
      return null;
    }
    
    const { margin, buyPrice, priceType, lastUpdated } = profitData;
    const priceTypeText = priceType === 'last_buy' ? 'last buy' : 'weighted_avg';
    
    const safeBuyPrice = typeof buyPrice === 'number' ? buyPrice : parseFloat(buyPrice) || 0;
    
    const tooltip = `Profit: ${margin > 0 ? '+' : ''}${margin.toFixed(2)}% based on ${priceTypeText} price ($${safeBuyPrice.toFixed(2)})${lastUpdated ? ` - Updated: ${new Date(lastUpdated).toLocaleTimeString()}` : ''}`;
    
    if (margin > 0) {
      return {
        color: '#00c853',
        text: `+${margin.toFixed(2)}%`,
        icon: '📈',
        tooltip: tooltip
      };
    } else if (margin < 0) {
      return {
        color: '#ff1744',
        text: `${margin.toFixed(2)}%`,
        icon: '📉',
        tooltip: tooltip
      };
    } else {
      return {
        color: '#b0b8c4',
        text: '0.00%',
        icon: '➖',
        tooltip: tooltip
      };
    }
  };

  const hasBuyData = (order) => {
    if (order.type !== 'sell') return false;
    
    const baseCurrency = order.pair?.split('/')[0];
    const userHolding = userHoldings[order.user_id]?.[baseCurrency];
    
    const hasData = userHolding && 
           userHolding.last_buy_price && 
           userHolding.last_buy_price > 0 &&
           parseFloat(userHolding.total_quantity) > 0;
    
    return hasData;
  };

  const getHoldingDetails = (order) => {
    if (order.type !== 'sell') return null;
    
    const baseCurrency = order.pair?.split('/')[0];
    const userHolding = userHoldings[order.user_id]?.[baseCurrency];
    
    if (!userHolding) {
      return null;
    }
    
    const priceInfo = getActualBuyPriceDisplay(order);
    
    const result = {
      averageBuyPrice: parseFloat(userHolding.average_buy_price || 0),
      firstBuyPrice: priceInfo?.firstBuyPrice,
      lastBuyPrice: priceInfo?.lastBuyPrice,
      displayPrice: priceInfo?.displayPrice,
      priceType: priceInfo?.priceType,
      totalQuantity: parseFloat(userHolding.total_quantity || 0),
      totalInvestment: parseFloat(userHolding.total_investment || 0),
      totalTrades: priceInfo?.totalTrades || 0,
      lastUpdated: priceInfo?.lastUpdated
    };
    
    return result;
  };

  const startEditOrder = (order) => {
    setEditingOrder(order);
    setEditPrice(order.price || '');
    setEditAmount(order.amount || '');
    setProfitMargin(0);
  };

  const cancelEdit = () => {
    setEditingOrder(null);
    setEditPrice('');
    setEditAmount('');
    setProfitMargin(0);
  };

  const handleProfitMarginChange = (margin) => {
    setProfitMargin(margin);
    
    if (editingOrder && editAmount) {
      const holding = getHoldingDetails(editingOrder);
      if (holding && holding.lastBuyPrice > 0) {
        const marginDecimal = margin / 100;
        const newPrice = holding.lastBuyPrice * (1 + marginDecimal);
        setEditPrice(newPrice.toFixed(6));
      }
    }
  };

  // FIXED: Update order price with broadcast
  const updateOrderPrice = async (orderId, newPrice, newAmount) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!newPrice || !newAmount) {
        alert('Please enter both price and amount');
        return;
      }

      const calculatedTotal = parseFloat(newPrice) * parseFloat(newAmount);
      
      const response = await axios.put(
        `https://trcsite.net/api/admin/orders/${orderId}/update-price`,
        {
          price: parseFloat(newPrice),
          amount: parseFloat(newAmount),
          total: calculatedTotal,
          profit_margin_applied: profitMargin
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        alert('Order updated successfully!');
        cancelEdit();
        
        // BROADCAST THE UPDATE
        if (socketRef.current) {
          socketRef.current.emit('broadcastOrderUpdate', response.data.order);
        }
        
        // Refresh data
        if (activeTab === 'all') {
          fetchOrders();
        } else {
          fetchPendingOrders();
        }
        fetchUserHoldings();
      }
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Failed to update order: ' + (error.response?.data?.error || error.message));
    }
  };

  const calculateTotal = (price, amount) => {
    if (!price || !amount) return '0.00';
    return (parseFloat(price) * parseFloat(amount)).toFixed(8);
  };

  // FIXED: Cancel order with broadcast
  const cancelOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`https://trcsite.net/api/admin/orders/${orderId}/cancel`, 
        { reason: 'Admin cancellation' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        // BROADCAST THE CANCELLATION
        if (socketRef.current) {
          socketRef.current.emit('broadcastOrderStatusChange', {
            ...response.data.order,
            status: 'cancelled'
          });
        }
        
        if (activeTab === 'all') {
          fetchOrders();
        } else {
          fetchPendingOrders();
        }
        alert('Order cancelled successfully');
      }
    } catch (error) {
      console.error('Error cancelling order:', error);
      alert('Failed to cancel order: ' + (error.response?.data?.error || error.message));
    }
  };

  // FIXED: Approve order with broadcast
  // In OrderManagement.js - FIXED approveOrder function
const approveOrder = async (orderId) => {
  if (!window.confirm('Are you sure you want to approve this order?')) {
    return;
  }

  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(`https://trcsite.net/api/admin/orders/${orderId}/approve`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    if (response.data.success) {
      console.log('✅ Order approved successfully:', response.data);
      alert('Order approved and executed successfully!');
      
      // 🔥 ENHANCED BROADCASTING - Send multiple events for guaranteed delivery
      if (socketRef.current) {
        const orderData = response.data.order;
        const tradeData = response.data.trade;
        const walletUpdates = response.data.walletUpdates;
        
        console.log('📢 Broadcasting order approval events...');
        
        // 1. Broadcast order status change
        socketRef.current.emit('broadcastOrderStatusChange', {
          ...orderData,
          status: 'filled',
          broadcast_type: 'admin_approval'
        });
        
        // 2. Broadcast order update (legacy support)
        socketRef.current.emit('broadcastOrderUpdate', {
          ...orderData,
          status: 'filled',
          broadcast_type: 'admin_approval'
        });
        
        // 3. Broadcast trade execution if available
        if (tradeData) {
          socketRef.current.emit('broadcastTradeUpdate', {
            ...tradeData,
            broadcast_type: 'admin_approval'
          });
        }
        
        // 4. Broadcast wallet updates
        if (walletUpdates) {
          socketRef.current.emit('broadcastHoldingsUpdate', {
            ...walletUpdates,
            broadcast_type: 'admin_approval'
          });
        }
        
        // 5. Send direct order filled event
        socketRef.current.emit('broadcastOrderFilled', {
          ...orderData,
          broadcast_type: 'admin_approval'
        });
        
        console.log('✅ All approval events broadcasted');
      }
      
      // Refresh data after short delay to ensure WebSocket delivery
      setTimeout(() => {
        if (activeTab === 'all') {
          fetchOrders();
        } else {
          fetchPendingOrders();
        }
      }, 500);
      
      setTimeout(() => {
        fetchUserHoldings();
      }, 1000);
    }
  } catch (error) {
    console.error('❌ Error approving order:', error);
    alert('Failed to approve order: ' + (error.response?.data?.error || error.message));
  }
};

  // FIXED: Reject order with broadcast
  const rejectOrder = async (orderId) => {
    const reason = prompt('Please enter the reason for rejection:');
    if (!reason) {
      alert('Rejection reason is required.');
      return;
    }

    if (!window.confirm('Are you sure you want to reject this order?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`https://trcsite.net/api/admin/orders/${orderId}/reject`, 
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        alert('Order rejected successfully!');
        
        // BROADCAST THE REJECTION
        if (socketRef.current) {
          socketRef.current.emit('broadcastOrderStatusChange', {
            ...response.data.order,
            status: 'rejected',
            rejection_reason: reason
          });
        }
        
        fetchPendingOrders();
      }
    } catch (error) {
      console.error('Error rejecting order:', error);
      alert('Failed to reject order: ' + (error.response?.data?.error || error.message));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'filled': return '#00c853';
      case 'open': return '#2962ff';
      case 'partial': return '#ffa726';
      case 'pending': return '#ff9800';
      case 'cancelled': return '#ff1744';
      case 'rejected': return '#f44336';
      default: return '#b0b8c4';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Trading';
      case 'partial': return 'PARTIALLY FILLED';
      default: return status.toUpperCase();
    }
  };

  const canEditOrder = (order) => {
    return ['pending', 'open', 'partial'].includes(order.status);
  };

  const renderRealLastBuyInfo = (order, holding) => {
    if (!holding || order.type !== 'sell') return null;

    const isRecent = holding.lastUpdated && (new Date() - new Date(holding.lastUpdated)) < 30000;
    
    return (
      <div style={{ fontSize: '10px', color: '#b0b8c4', marginTop: '2px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>Real last buy: ${holding.lastBuyPrice?.toFixed(6) || 'N/A'}</span>
          {isRecent && (
            <span 
              style={{ 
                color: '#00c853',
                fontSize: '8px',
                animation: 'pulse 1s infinite'
              }}
              title="Recently updated"
            >
              ●
            </span>
          )}
        </div>
        {holding.totalQuantity > 0 && (
          <span> ({holding.totalQuantity.toFixed(6)} coins)</span>
        )}
        {holding.lastUpdated && (
          <div style={{ fontSize: '8px', color: '#666', marginTop: '1px' }}>
            updated: {new Date(holding.lastUpdated).toLocaleTimeString()}
          </div>
        )}
      </div>
    );
  };

  const renderOrdersTable = (ordersData, isLoading) => {
    if (isLoading || holdingsLoading) {
      return <div className="loading">Loading orders...</div>;
    }

    if (ordersData.length === 0) {
      return (
        <div style={{ 
          textAlign: 'center', 
          color: '#b0b8c4', 
          padding: '40px',
          border: '1px dashed #344258',
          borderRadius: '4px',
          marginTop: '20px'
        }}>
          No orders found
        </div>
      );
    }

    return (
      <div className="table-container">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #344258' }}>
              <th style={{ padding: '12px', textAlign: 'left', color: '#b0b8c4' }}>User</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#b0b8c4' }}>Pair</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#b0b8c4' }}>Type</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#b0b8c4' }}>Method</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#b0b8c4' }}>Price</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#b0b8c4' }}>Amount</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#b0b8c4' }}>Total</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#b0b8c4' }}>Profit Margin</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#b0b8c4' }}>Status</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#b0b8c4' }}>Created</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#b0b8c4' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {ordersData.map((order) => {
              const profitData = calculateProfitMargin(order);
              const profitInfo = getProfitMarginInfo(profitData, order);
              const userHasBuyData = hasBuyData(order);
              const holding = getHoldingDetails(order);
              
              return (
                <tr key={order.id} style={{ borderBottom: '1px solid #2a3b52' }}>
                  <td style={{ padding: '12px' }}>
                    <div>
                      <div style={{ fontWeight: 'bold' }}>{order.user_name}</div>
                      <div style={{ fontSize: '12px', color: '#b0b8c4' }}>{order.user_email}</div>
                      {holding && order.type === 'sell' && (
                        <div style={{ fontSize: '10px', color: '#00c853', marginTop: '2px' }}>
                          Holds: {holding.totalQuantity.toFixed(6)} {order.pair?.split('/')[0]}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>{order.pair}</td>
                  <td style={{ 
                    padding: '12px', 
                    color: order.type === 'buy' ? '#00c853' : '#ff1744',
                    fontWeight: 'bold'
                  }}>
                    {order.type.toUpperCase()}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      backgroundColor: order.order_type === 'market' ? '#2962ff' : '#ff9800',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {order.order_type?.toUpperCase() || 'LIMIT'}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>
                    {editingOrder?.id === order.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <input
                          type="number"
                          step="0.000001"
                          value={editPrice}
                          onChange={(e) => setEditPrice(e.target.value)}
                          style={{
                            padding: '6px 8px',
                            border: '1px solid #344258',
                            borderRadius: '4px',
                            backgroundColor: '#1a2332',
                            color: '#ffffff',
                            width: '120px',
                            fontSize: '12px'
                          }}
                          placeholder="Enter price"
                        />
                        <div style={{ fontSize: '10px', color: '#b0b8c4' }}>
                          Current: {order.price ? `$${parseFloat(order.price).toFixed(6)}` : 'Market'}
                        </div>
                      </div>
                    ) : (
                      order.price ? `$${parseFloat(order.price).toFixed(6)}` : 'Market'
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    {editingOrder?.id === order.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <input
                          type="number"
                          step="0.000001"
                          value={editAmount}
                          onChange={(e) => setEditAmount(e.target.value)}
                          style={{
                            padding: '6px 8px',
                            border: '1px solid #344258',
                            borderRadius: '4px',
                            backgroundColor: '#1a2332',
                            color: '#ffffff',
                            width: '120px',
                            fontSize: '12px'
                          }}
                          placeholder="Enter amount"
                        />
                        <div style={{ fontSize: '10px', color: '#b0b8c4' }}>
                          Current: {parseFloat(order.amount).toFixed(6)}
                        </div>
                      </div>
                    ) : (
                      `${parseFloat(order.amount).toFixed(6)} ${order.pair?.split('/')[0]}`
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    {editingOrder?.id === order.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ 
                          color: '#ffffff', 
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          ${calculateTotal(editPrice, editAmount)}
                        </span>
                        <div style={{ fontSize: '10px', color: '#b0b8c4' }}>
                          Auto-calculated
                        </div>
                      </div>
                    ) : (
                      `$${parseFloat(order.total).toFixed(2)}`
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    {editingOrder?.id === order.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '11px', color: '#b0b8c4', minWidth: '60px' }}>
                            Margin: {profitMargin}%
                          </span>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            step="0.5"
                            value={profitMargin}
                            onChange={(e) => handleProfitMarginChange(parseFloat(e.target.value))}
                            style={{
                              width: '100px',
                              height: '4px',
                              borderRadius: '2px',
                              background: '#344258',
                              outline: 'none'
                            }}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {[0, 10, 20, 40, 60, 80, 100].map(percent => (
                            <button
                              key={percent}
                              onClick={() => handleProfitMarginChange(percent)}
                              style={{
                                padding: '2px 6px',
                                backgroundColor: profitMargin === percent ? '#2962ff' : '#344258',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                fontSize: '10px',
                                minWidth: '30px'
                              }}
                            >
                              {percent}%
                            </button>
                          ))}
                        </div>
                        {userHasBuyData && holding && (
                          <div style={{ fontSize: '10px', color: '#00c853' }}>
                            Based on REAL last buy: ${holding.lastBuyPrice.toFixed(6)}
                          </div>
                        )}
                      </div>
                    ) : profitInfo ? (
                      <div 
                        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                        title={profitInfo.tooltip}
                      >
                        <span style={{ fontSize: '14px' }}>{profitInfo.icon}</span>
                        <span style={{ 
                          color: profitInfo.color,
                          fontWeight: 'bold',
                          fontSize: '12px'
                        }}>
                          {profitInfo.text}
                        </span>
                      </div>
                    ) : order.type === 'sell' ? (
                      <div 
                        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                        title="No real buy data available for profit calculation"
                      >
                        <span style={{ fontSize: '14px' }}>📭</span>
                        <span style={{ 
                          color: '#ff9800',
                          fontSize: '11px'
                        }}>
                          No buy data
                        </span>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '14px' }}>🛒</span>
                        <span style={{ 
                          color: '#b0b8c4',
                          fontSize: '11px'
                        }}>
                          Buy order
                        </span>
                      </div>
                    )}
                    {!editingOrder && order.type === 'sell' && renderRealLastBuyInfo(order, holding)}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      color: getStatusColor(order.status),
                      fontWeight: 'bold',
                      fontSize: '12px'
                    }}>
                      {getStatusText(order.status)}
                    </span>
                    {order.status === 'partial' && order.filled_amount > 0 && (
                      <div style={{ fontSize: '11px', color: '#b0b8c4' }}>
                        Filled: {parseFloat(order.filled_amount).toFixed(6)}
                      </div>
                    )}
                    {order.rejection_reason && (
                      <div style={{ fontSize: '11px', color: '#ff1744', marginTop: '4px' }}>
                        Reason: {order.rejection_reason}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '12px', fontSize: '12px' }}>
                    {new Date(order.created_at).toLocaleString()}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '120px' }}>
                      {editingOrder?.id === order.id ? (
                        <>
                          <button
                            onClick={() => updateOrderPrice(order.id, editPrice, editAmount)}
                            disabled={!editPrice || !editAmount}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: editPrice && editAmount ? '#00c853' : '#666',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: editPrice && editAmount ? 'pointer' : 'not-allowed',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#b0b8c4',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          {canEditOrder(order) && (
                            <button
                              onClick={() => startEditOrder(order)}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#2962ff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: 'bold'
                              }}
                            >
                              ✏️ Edit
                            </button>
                          )}
                          {order.status === 'pending' && (
                            <>
                              <button
                                onClick={() => approveOrder(order.id)}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#00c853',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => rejectOrder(order.id)}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#ff1744',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                Reject
                              </button>
                            </>
                          )}
                          {(order.status === 'open' || order.status === 'partial') && (
                            <button
                              onClick={() => cancelOrder(order.id)}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#ff1744',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              Cancel
                            </button>
                          )}
                          {order.status === 'rejected' && (
                            <span style={{ color: '#ff1744', fontSize: '12px', textAlign: 'center' }}>
                              Rejected
                            </span>
                          )}
                          {order.status === 'cancelled' && (
                            <span style={{ color: '#b0b8c4', fontSize: '12px', textAlign: 'center' }}>
                              Cancelled
                            </span>
                          )}
                          {order.status === 'filled' && (
                            <span style={{ color: '#00c853', fontSize: '12px', textAlign: 'center' }}>
                              Filled
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPagination = (currentPage, totalPages, setCurrentPage) => {
    if (totalPages <= 1) return null;

    return (
      <div className="pagination" style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
        <button
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          style={{
            padding: '8px 16px',
            border: '1px solid #344258',
            backgroundColor: currentPage === 1 ? '#2a3b52' : '#2962ff',
            color: '#ffffff',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            borderRadius: '4px'
          }}
        >
          Previous
        </button>
        
        <span style={{ color: '#b0b8c4', padding: '8px 16px' }}>
          Page {currentPage} of {totalPages}
        </span>
        
        <button
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          style={{
            padding: '8px 16px',
            border: '1px solid #344258',
            backgroundColor: currentPage === totalPages ? '#2a3b52' : '#2962ff',
            color: '#ffffff',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            borderRadius: '4px'
          }}
        >
          Next
        </button>
      </div>
    );
  };

  const renderConnectionStatus = () => (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      gap: '10px',
      marginBottom: '15px',
      padding: '12px',
      backgroundColor: isConnected ? '#1b5e20' : '#b71c1c',
      color: 'white',
      borderRadius: '4px',
      fontSize: '14px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: isConnected ? 
              (connectionQuality === 'good' ? '#4caf50' : '#ff9800') : '#f44336',
            animation: isConnected ? 'pulse 2s infinite' : 'none'
          }}></div>
          <span>
            {isConnected ? 
              `🟢 Real-time: ${connectionQuality === 'good' ? 'Connected' : 'Poor Connection'}` : 
              '🔴 Real-time: Disconnected'}
          </span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          {/* Auto-refresh Controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                checked={autoRefresh}
                onChange={toggleAutoRefresh}
                style={{ margin: 0, cursor: 'pointer' }}
              />
              Auto Refresh
            </label>
            
            {autoRefresh && (
              <select 
                value={refreshInterval} 
                onChange={(e) => handleIntervalChange(Number(e.target.value))}
                style={{
                  padding: '2px 4px',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '2px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'white',
                  fontSize: '11px',
                  cursor: 'pointer'
                }}
              >
                <option value={5000}>5s</option>
                <option value={10000}>10s</option>
                <option value={30000}>30s</option>
                <option value={60000}>1m</option>
              </select>
            )}
          </div>

          <button
            onClick={() => refreshAllData(true)}
            disabled={isRefreshing}
            style={{
              padding: '4px 8px',
              backgroundColor: isRefreshing ? '#666' : '#2962ff',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: isRefreshing ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              opacity: isRefreshing ? 0.7 : 1
            }}
          >
            <span style={{ 
              animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
              display: 'inline-block'
            }}>🔄</span>
            {isRefreshing ? 'Refreshing...' : 'Refresh Now'}
          </button>
        </div>
      </div>

      <div style={{ 
        display: 'flex', 
        gap: '15px', 
        fontSize: '12px',
        opacity: 0.9,
        flexWrap: 'wrap'
      }}>
        {lastUpdate && (
          <span>Last update: {lastUpdate.toLocaleTimeString()}</span>
        )}
        {lastManualRefresh && (
          <span>Manual refresh: {lastManualRefresh.toLocaleTimeString()}</span>
        )}
        <span>Total updates: {updateCount}</span>
        <span>Holdings updates: {holdingsUpdateCount}</span>
        {autoRefresh && (
          <span style={{ color: '#ffeb3b' }}>
            Auto-refresh: {refreshInterval/1000}s {isConnected ? '(WebSocket)' : '(Polling)'}
          </span>
        )}
        {!autoRefresh && (
          <span style={{ color: '#ff9800' }}>
            Manual mode
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="management-section">
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      
      <div className="section-header">
        <h2>Order Management</h2>
        
        {renderConnectionStatus()}
        
        {/* <div style={{ 
          backgroundColor: '#2a3b52', 
          padding: '15px', 
          borderRadius: '6px', 
          marginBottom: '20px',
          border: '1px solid #2962ff'
        }}>
          <h4 style={{ color: '#2962ff', margin: '0' }}>
            ⓘ Real-time Order Management
          </h4>
          <p style={{ color: '#b0b8c4', margin: '5px 0 0 0', fontSize: '14px' }}>
            • <strong>Auto-refresh:</strong> {autoRefresh ? `Every ${refreshInterval/1000} seconds` : 'Disabled'} {isConnected ? '(WebSocket)' : '(HTTP Polling)'}<br/>
            • <strong>Connection Quality:</strong> {connectionQuality === 'good' ? 'Good' : connectionQuality === 'poor' ? 'Poor' : 'Disconnected'}<br/>
            • <strong>Last Update:</strong> {lastUpdate ? lastUpdate.toLocaleTimeString() : 'Never'}<br/>
            • <strong>Total Updates:</strong> {updateCount} (Holdings: {holdingsUpdateCount})<br/>
            • <strong>Broadcasting:</strong> All admin actions broadcast to Trading component
          </p>
        </div> */}
        
        <div className="admin-nav" style={{ marginBottom: '20px', borderBottom: '1px solid #344258' }}>
          <button
            onClick={() => setActiveTab('all')}
            style={{
              padding: '10px 20px',
              backgroundColor: activeTab === 'all' ? '#2962ff' : 'transparent',
              color: '#ffffff',
              border: 'none',
              borderBottom: activeTab === 'all' ? '2px solid #2962ff' : 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'all' ? 'bold' : 'normal'
            }}
          >
            All Orders
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            style={{
              padding: '10px 20px',
              backgroundColor: activeTab === 'pending' ? '#2962ff' : 'transparent',
              color: '#ffffff',
              border: 'none',
              borderBottom: activeTab === 'pending' ? '2px solid #2962ff' : 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'pending' ? 'bold' : 'normal'
            }}
          >
            Trading
            {pendingOrders.length > 0 && (
              <span style={{
                backgroundColor: '#ff9800',
                color: 'white',
                borderRadius: '50%',
                padding: '2px 6px',
                fontSize: '12px',
                marginLeft: '8px'
              }}>
                {pendingOrders.length}
              </span>
            )}
          </button>
        </div>

        {activeTab === 'all' && (
          <div className="filters" style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              style={{
                padding: '8px 12px',
                border: '1px solid #344258',
                borderRadius: '4px',
                backgroundColor: '#1a2332',
                color: '#ffffff'
              }}
            >
              <option value="">All Status</option>
              <option value="open">Open</option>
              <option value="pending">Pending</option>
              <option value="filled">Filled</option>
              <option value="cancelled">Cancelled</option>
              <option value="partial">Partial</option>
              <option value="rejected">Rejected</option>
            </select>

            <select
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              style={{
                padding: '8px 12px',
                border: '1px solid #344258',
                borderRadius: '4px',
                backgroundColor: '#1a2332',
                color: '#ffffff'
              }}
            >
              <option value="">All Types</option>
              <option value="buy">Buy</option>
              <option value="sell">Sell</option>
            </select>

            <input
              type="text"
              placeholder="Filter by pair (e.g., BTC/USDT)"
              value={filters.pair}
              onChange={(e) => setFilters(prev => ({ ...prev, pair: e.target.value }))}
              style={{
                padding: '8px 12px',
                border: '1px solid #344258',
                borderRadius: '4px',
                backgroundColor: '#1a2332',
                color: '#ffffff',
                width: '200px'
              }}
            />

            <button
              onClick={() => setFilters({ status: '', type: '', pair: '' })}
              style={{
                padding: '8px 12px',
                border: '1px solid #344258',
                borderRadius: '4px',
                backgroundColor: '#ff1744',
                color: '#ffffff',
                cursor: 'pointer'
              }}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {activeTab === 'all' ? (
        <>
          {renderOrdersTable(orders, loading)}
          {renderPagination(currentPage, totalPages, setCurrentPage)}
        </>
      ) : (
        <>
          <div style={{ 
            backgroundColor: '#2a3b52', 
            padding: '15px', 
            borderRadius: '6px', 
            marginBottom: '20px',
            border: '1px solid #ff9800'
          }}>
            <h4 style={{ color: '#ff9800', margin: '0' }}>
              ⓘ Pending Orders Require Approval
            </h4>
            <p style={{ color: '#b0b8c4', margin: '5px 0 0 0', fontSize: '14px' }}>
              You can edit prices/amounts or approve/reject these orders. Profit margins show real user purchase history.
            </p>
          </div>
          
          {renderOrdersTable(pendingOrders, pendingLoading)}
          {renderPagination(pendingCurrentPage, pendingTotalPages, setPendingCurrentPage)}
        </>
      )}

      <div style={{ 
        marginTop: '30px', 
        padding: '20px', 
        backgroundColor: '#1a2332', 
        borderRadius: '6px',
        border: '1px solid #344258'
      }}>
        <h4 style={{ color: '#b0b8c4', marginBottom: '15px' }}>Order Statistics</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#b0b8c4', fontSize: '14px' }}>Total Orders</div>
            <div style={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold' }}>{orders.length}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#b0b8c4', fontSize: '14px' }}>Trading</div>
            <div style={{ color: '#ff9800', fontSize: '24px', fontWeight: 'bold' }}>{pendingOrders.length}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#b0b8c4', fontSize: '14px' }}>Open Orders</div>
            <div style={{ color: '#2962ff', fontSize: '24px', fontWeight: 'bold' }}>
              {orders.filter(o => o.status === 'open').length}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#b0b8c4', fontSize: '14px' }}>Filled Orders</div>
            <div style={{ color: '#00c853', fontSize: '24px', fontWeight: 'bold' }}>
              {orders.filter(o => o.status === 'filled').length}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderManagement;